/* Lazy Vimeo background embed.

   Mounts a player.vimeo.com iframe once the element nears the viewport, or
   immediately when data-eager is present. The poster remains underneath while
   the player boots and as a fallback if it never does.

   Cover-cropping is done here rather than in CSS because browsers do not apply
   object-fit to an iframe, and the source aspect varies per video — the hero
   clips are a mix of 16:9 and 9:16, so no hardcoded ratio is correct. The
   ratio is read off the poster, which is a frame of the video and therefore
   always matches it. `data-aspect` (w/h) overrides when there is no poster. */
class VimeoLazy extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    /* Carousel clones stay poster-only. A mounted embed is a whole
       cross-origin player.vimeo.com document, so an infinite reel that
       triples its tiles would otherwise treble the embed count for copies
       that are only on screen during a wrap. The reel script sets this
       attribute on the tiles it clones. */
    if (this.dataset.clone) return;
    if (this.dataset.eager != null) {
      this.mount();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        this.mount();
      },
      { rootMargin: '200px' }
    );
    observer.observe(this);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  /* Public and unlisted Vimeo links use different URL shapes. The privacy
     hash must be retained before player options or Vimeo leaves the poster
     in place while the embed fails to play. */
  static embedUrl(sourceUrl, fallbackUrl) {
    const embed = new URL(fallbackUrl);
    if (!sourceUrl) return embed.href;

    try {
      const raw = sourceUrl.trim();
      const source = /^\d+$/.test(raw) ? new URL(`https://vimeo.com/${raw}`) : new URL(raw);
      if (!['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(source.hostname)) return embed.href;
      const parts = source.pathname.split('/').filter(Boolean);
      const idIndex = parts.findIndex((part) => /^\d+$/.test(part));
      if (idIndex < 0) return embed.href;
      embed.pathname = `/video/${parts[idIndex]}`;
      const pathHash = parts[idIndex + 1];
      const hash = source.searchParams.get('h') || (pathHash && /^[a-f\d]+$/i.test(pathHash) ? pathHash : '');
      if (hash) {
        const options = [...embed.searchParams.entries()];
        embed.search = '';
        embed.searchParams.set('h', hash);
        for (const [key, value] of options) embed.searchParams.append(key, value);
      }
    } catch (_) {
      // Keep the existing embed URL for malformed legacy values.
    }
    return embed.href;
  }

  mount() {
    /* data-clone can be set after connectedCallback has already scheduled an
       observer — the reel marks its poster-only sets once the tiles are in
       place — so the flag is re-checked here rather than only on connect. */
    if (this.dataset.clone) return;
    this.dataset.loaded = 'true';
    const frame = document.createElement('iframe');
    frame.src = VimeoLazy.embedUrl(this.dataset.vimeoUrl, this.dataset.src);
    frame.setAttribute('allow', 'autoplay; picture-in-picture');
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('title', this.getAttribute('aria-label') || '');
    /* data-ready gates the poster fade, and is set on the iframe's own load
       event — data-loaded only means the embed was mounted, which is several
       hundred ms before anything is painted. */
    frame.addEventListener('load', () => {
      this.dataset.ready = 'true';
    });
    /* Append rather than replace: the poster stays in the DOM underneath. */
    this.appendChild(frame);
    this.frame = frame;

    this.cover();
    const poster = this.querySelector('img');
    if (poster && !poster.complete) poster.addEventListener('load', () => this.cover());
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.cover());
      this.resizeObserver.observe(this);
    }
  }

  /* Size the iframe to cover this element without distorting the video:
     match whichever axis would otherwise leave a gap and let the other
     overflow, to be clipped by the host's overflow:hidden. */
  cover() {
    if (!this.frame) return;
    const width = this.offsetWidth;
    const height = this.offsetHeight;
    if (!width || !height) return;

    let ratio = parseFloat(this.dataset.aspect || '');
    const poster = this.querySelector('img');
    if (!ratio && poster && poster.naturalWidth) {
      ratio = poster.naturalWidth / poster.naturalHeight;
    }
    if (!ratio) return;

    /* Both axes in px, never a percentage. A percentage resolves against the
       host, and a host sized by `aspect-ratio` with `height: auto` grows with
       its content -- so `height: 100%` made the iframe depend on a height that
       depended on the iframe. The ResizeObserver then re-ran this on every
       change and ratcheted the box outwards (the PDP accordion settled at 851px
       for a 700px frame, and dragged the width to 479px with it). It only tips
       into that branch when the poster ratio and the box ratio are nearly
       equal, which is exactly the case when the box is authored to match. */
    if (ratio > width / height) {
      // Video is wider than the box: fill the height, overflow the width.
      this.frame.style.height = height + 'px';
      this.frame.style.width = Math.ceil(height * ratio) + 'px';
    } else {
      this.frame.style.width = width + 'px';
      this.frame.style.height = Math.ceil(width / ratio) + 'px';
    }
  }
}
customElements.define('vimeo-lazy', VimeoLazy);
